import { useQuery, useQueries } from 'react-query';
import { NEW_DASHBOARD_CARDS } from './dashboardNewCardsConfig';

const BUYER_TYPE_ID = 3;

const fetchWithToken = async (url: string, token: string, logout: any) => {
  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    if (response.status === 401 && logout) {
      logout();
    }
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return response.json();
};

// The user type the dashboard acts as: Buyer when the header switch is on, otherwise the real user type
const getEffectiveTypeId = (currentUser: any, isBuyerView: boolean) =>
  isBuyerView ? BUYER_TYPE_ID : currentUser?.user_type?.id;

// user_type_id always comes from the logged-in user (auth/verify-token), not from the switch
const getUserTypeId = (currentUser: any) => currentUser?.user_type?.id;

// Builds user_type_id/userId params
const buildUserParams = (currentUser: any) => {
  const params = new URLSearchParams();
  const typeId = getUserTypeId(currentUser);
  if (typeId != null) {
    params.append('user_type_id', typeId.toString());
  }
  if (currentUser?.id != null) {
    params.append('userId', currentUser.id.toString());
  }
  return params;
};

export const useTotalProductsQuery = (apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  return useQuery(
    ['totalProducts', currentUser?.id, currentUser?.user_type?.id, isBuyerView],
    async () => {
      const params = buildUserParams(currentUser);
      return fetchWithToken(`${apiUrl}/dashboard/GetAllProducts?${params.toString()}`, token, logout);
    },
    {
      enabled: !!currentUser && !!token,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useTotalPriceMappingFeedQuery = (apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  return useQuery(
    ['totalPriceMappingFeed', currentUser?.id, currentUser?.user_type?.id, isBuyerView],
    async () => {
      const params = buildUserParams(currentUser);
      return fetchWithToken(`${apiUrl}/sellerProduct/price-feed/count?${params.toString()}`, token, logout);
    },
    {
      enabled: !!currentUser && !!token && getEffectiveTypeId(currentUser, isBuyerView) !== BUYER_TYPE_ID,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useLastProductQuery = (apiUrl: string, token: string, logout: any) => {
  return useQuery(
    ['lastProduct'],
    () => fetchWithToken(`${apiUrl}/product/IbsSyncDetails`, token, logout),
    {
      enabled: !!token,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useScraperAccountStatusQuery = (apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  return useQuery(
    ['scraperAccountStatus', isBuyerView],
    () => fetchWithToken(`${apiUrl}/dashboard/ScraperAccountStatus`, token, logout),
    {
      enabled: !!token && !!currentUser && getEffectiveTypeId(currentUser, isBuyerView) !== BUYER_TYPE_ID,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useTotalBuyersQuery =(apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  return useQuery(
    ['totalBuyers', currentUser?.id, currentUser?.user_type?.id, isBuyerView],
    async () => {
      const params = buildUserParams(currentUser);
      return fetchWithToken(`${apiUrl}/dashboard/GetAllBuyer?${params.toString()}`, token, logout);
    },
    {
      enabled: !!currentUser && !!token && getEffectiveTypeId(currentUser, isBuyerView) !== BUYER_TYPE_ID,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useTotalAdminsQuery = (apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  return useQuery(
    ['totalAdmins', isBuyerView],
    () => fetchWithToken(`${apiUrl}/dashboard/GetAllAdmin`, token, logout),
    {
      enabled: !!token && getEffectiveTypeId(currentUser, isBuyerView) !== BUYER_TYPE_ID,
      staleTime: 1000 * 60 * 10,
      cacheTime: 1000 * 60 * 10,
    }
  );
};

export const useNewCardQueries = (apiUrl: string, token: string, currentUser: any, logout: any, isBuyerView: boolean) => {
  const effectiveTypeId = getEffectiveTypeId(currentUser, isBuyerView);
  return useQueries(
    NEW_DASHBOARD_CARDS.map((card) => {
      return {
        queryKey: ['newCard', card.key, currentUser?.id, isBuyerView],
        queryFn: async () => {
          const params = new URLSearchParams();
          const userTypeId = getUserTypeId(currentUser);
          if (userTypeId != null) {
            params.append('user_type_id', userTypeId.toString());
          }
          if (currentUser?.id != null) {
            params.append('userId', currentUser.id.toString());
          }
          params.append('page', '1');
          params.append('limit', '1');
          return fetchWithToken(`${apiUrl}/dashboard/${card.endpoint}?${params.toString()}`, token, logout);
        },
        enabled: !!currentUser && !!token && (effectiveTypeId === BUYER_TYPE_ID || !!card.showForAdmin),
        staleTime: 1000 * 60 * 10,
        cacheTime: 1000 * 60 * 10,
      };
    })
  );
};
